import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen } from "lucide-react";
import FitText from "@/components/tv/FitText";
import { ClassesSlide, GoalSlide, RewardsSlide, TopReadersSlide } from "@/components/tv/TvSlides";
import { formatNumber } from "@/lib/format";
import { buildSlides, phaseLine, tvMoney } from "@/lib/tvDisplay";

// How long the outgoing slide stays mounted for its exit animation.
const EXIT_MS = 500;

/**
 * The TV display's slideshow, laid out for the 1920×1080 stage: a header
 * with the school, campaign and totals, the rotating slides, and a
 * footer with the slide timer and donation link. `content` is the
 * TvDisplays::Content props plus `school` and `donate_url`. Used by the
 * display itself (pages/TvDisplays/Show) and the settings page's preview,
 * which is `interactive`: its footer segments jump between slides.
 */
export default function TvSlideshow({ content, interactive = false }) {
  const { school, campaign, rewards, top_readers: topReaders, classrooms, donate_url: donateUrl, parts } = content;
  if (!campaign) return <Waiting school={school} />;

  return (
    <Slideshow
      school={school}
      campaign={campaign}
      rewards={rewards}
      topReaders={topReaders}
      classrooms={classrooms}
      donateUrl={donateUrl}
      parts={parts}
      interactive={interactive}
    />
  );
}

function Slideshow({ school, campaign, rewards, topReaders, classrooms, donateUrl, parts, interactive }) {
  const slides = useMemo(
    () => buildSlides({ rewards, top_readers: topReaders, classrooms, parts }),
    [rewards, topReaders, classrooms, parts],
  );
  // Tracked by key, so a refresh that adds a page doesn't jump slides.
  const [currentKey, setCurrentKey] = useState(slides[0].key);
  const [leavingKey, setLeavingKey] = useState(null);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const index = Math.max(0, slides.findIndex((s) => s.key === currentKey));
  const current = slides[index];
  const leaving = leavingKey && leavingKey !== current.key ? slides.find((s) => s.key === leavingKey) : null;

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = setTimeout(() => {
      const list = slidesRef.current;
      const at = Math.max(0, list.findIndex((s) => s.key === current.key));
      setLeavingKey(current.key);
      setCurrentKey(list[(at + 1) % list.length].key);
    }, current.seconds * 1000);
    return () => clearTimeout(timer);
  }, [current.key, current.seconds, slides.length]);

  useEffect(() => {
    if (!leavingKey) return undefined;
    const timer = setTimeout(() => setLeavingKey(null), EXIT_MS);
    return () => clearTimeout(timer);
  }, [leavingKey]);

  const render = (slide) => {
    switch (slide.type) {
      case "rewards":
        return <RewardsSlide slide={slide} rewards={rewards} />;
      case "readers":
        return <TopReadersSlide readers={topReaders} />;
      case "classes":
        return <ClassesSlide slide={slide} />;
      default:
        return <GoalSlide campaign={campaign} rewards={rewards} />;
    }
  };

  return (
    <div className="flex h-full flex-col px-20 pb-8 pt-12">
      <Header school={school} campaign={campaign} showTotals={current.type !== "goal"} />

      <main className="relative mt-10 min-h-0 flex-1">
        {/* Keyed by slide, so the outgoing slide keeps its element (and its
            fitted text) and only swaps animation, not remounts. */}
        {[leaving, current].filter(Boolean).map((slide) => (
          <div
            key={slide.key}
            className={`absolute inset-0 ${slide === current ? "tv-enter" : "tv-exit"}`}
            aria-hidden={slide === current ? undefined : "true"}
            data-slide={slide === current ? slide.key : undefined}
          >
            {render(slide)}
          </div>
        ))}
      </main>

      <Footer slides={slides} index={index} donateUrl={donateUrl} onPick={interactive ? setCurrentKey : undefined} />
    </div>
  );
}

function Header({ school, campaign, showTotals }) {
  return (
    <header className="flex items-center justify-between gap-12">
      <div className="min-w-0 flex-1">
        <FitText as="h1" max={64} min={36} height={80} lineHeight={1.1} className="font-display font-semibold text-foreground">
          {school.name}
        </FitText>
        <FitText max={30} min={22} height={58} lineHeight={1.3} className="mt-2 text-muted-foreground">
          <span className="font-medium text-foreground">{campaign.name}</span>
          <span className="mx-4 text-border">|</span>
          {phaseLine(campaign)}
        </FitText>
      </div>
      <div
        className={`flex shrink-0 gap-12 transition-opacity duration-700 motion-reduce:transition-none ${showTotals ? "opacity-100" : "opacity-0"}`}
        aria-hidden={!showTotals}
      >
        <HeaderTotal label="raised" value={tvMoney(campaign.raised_cents)} />
        <HeaderTotal label="minutes read" value={formatNumber(campaign.minutes_logged)} />
      </div>
    </header>
  );
}

function HeaderTotal({ label, value }) {
  return (
    <div className="text-right">
      <p className="font-display text-[56px] font-semibold leading-none text-foreground font-tabular">{value}</p>
      <p className="mt-2 text-[24px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

// One segment per slide; the current one fills over its time on screen.
// In the settings preview (`onPick`) each segment jumps to its slide.
function Footer({ slides, index, donateUrl, onPick }) {
  const current = slides[index];

  return (
    <footer className="mt-8 flex items-center justify-between gap-12">
      <div className="flex items-center gap-3">
        {slides.length > 1 &&
          slides.map((slide, i) => {
            const fill = (
              <>
                {i < index && <span className="block h-full w-full bg-muted" />}
                {i === index && (
                  <span
                    key={`${current.key}-fill`}
                    className="tv-fill block h-full w-full bg-primary"
                    style={{ "--tv-slide-ms": `${current.seconds * 1000}ms` }}
                  />
                )}
              </>
            );
            return onPick ? (
              <button
                key={slide.key}
                type="button"
                onClick={() => onPick(slide.key)}
                aria-label={`Show slide ${i + 1}`}
                className="h-2.5 w-16 cursor-pointer overflow-hidden rounded-full bg-border outline-offset-8"
              >
                {fill}
              </button>
            ) : (
              <span key={slide.key} className="h-2.5 w-16 overflow-hidden rounded-full bg-border">
                {fill}
              </span>
            );
          })}
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-8 text-muted-foreground">
        {donateUrl && (
          <FitText max={26} min={18} height={47} lineHeight={1.3} className="flex-1 text-right">
            Support our read-a-thon at <span className="font-semibold text-foreground">{donateUrl.replace(/^https?:\/\//, "")}</span>
          </FitText>
        )}
        <p className="shrink-0 font-display text-[32px] font-semibold text-foreground">
          PagePledge<span className="text-primary">.</span>
        </p>
      </div>
    </footer>
  );
}

// A valid code with nothing to show yet: no campaign running, or the
// last one finished more than a few weeks ago.
function Waiting({ school }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-24 text-center">
      <span className="flex h-28 w-28 items-center justify-center rounded-full bg-primary/10 text-primary">
        <BookOpen className="h-14 w-14" strokeWidth={2} />
      </span>
      <h1 className="mt-10 font-display text-[88px] font-semibold leading-tight text-foreground">{school.name}</h1>
      <p className="mt-6 text-[40px] text-muted-foreground">Our next read-a-thon is on its way.</p>
      <p className="mt-3 text-[30px] text-muted-foreground">This screen starts showing it by itself once it begins.</p>
      <p className="absolute bottom-12 font-display text-[32px] font-semibold text-foreground">
        PagePledge<span className="text-primary">.</span>
      </p>
    </div>
  );
}
